'use client';

import type { ReactNode } from 'react';
import { QueryProvider } from '../../providers/QueryProvider';
import { ModalStackProvider } from '../ModalStackProvider';
import { WebRunnerRequestUiProvider } from '../RunnerRequestUiProvider';

type RootProvidersProps = {
  children: ReactNode;
};

/**
 * 루트 공급자 스택: 공용 러너 요청 UI 런타임은 여기서 한 번만 공급한다.
 *
 * 배경(#2353·#2356): 상세 본문이 공용 패키지로 이전될 때 web 래퍼가 provider를 씌우지 않아
 * `RunnerRequestUiProvider is required`로 화면이 죽었고, 컴포넌트별 공급 구조에서는 새 래퍼를
 * 추가할 때마다 같은 누락이 재발할 수 있어 공급 위치를 루트로 옮겼다.
 * webRunnerRequestUiRuntime은 훅 참조만 담은 정적 값이라 마운트가 무비용이다.
 * Portal(PortalAppProviders → PortalRunnerRequestProvider)도 같은 구조다.
 *
 * 중첩 순서는 QueryClient 바깥·RunnerRequestUi 안쪽으로 고정한다. 런타임이 훅 기반 값으로
 * 바뀌어도 QueryClient에 접근할 수 있게 하며, Storybook 데코레이터도 같은 순서를 유지한다
 * (web/.storybook/preview.tsx).
 *
 * 예외: app/global-error.tsx는 루트 레이아웃을 대체하는 별도 루트라 이 스택 바깥이다.
 * 공용 러너 요청 UI를 에러 화면에 넣지 말 것.
 */
export const RootProviders = ({ children }: RootProvidersProps) => (
  <QueryProvider>
    <ModalStackProvider>
      <WebRunnerRequestUiProvider>{children}</WebRunnerRequestUiProvider>
    </ModalStackProvider>
  </QueryProvider>
);
